/**
 * Gradient Data Table Component
 *
 * Displays the well gauging data used for groundwater gradient calculation.
 * Shows water table elevations, coordinates, and calculated gradient values
 * per SOP T604 methodology.
 *
 * Features:
 * - Well gauging data table with water elevations
 * - Gradient summary statistics
 * - Flow direction indicator
 * - Data source badges
 */

import { useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTrendingDownIcon,
  MapPinIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { SiteGradientMap, GradientWellReading } from '../../api/polygon';

// ============================================================================
// TYPES
// ============================================================================

export interface GradientDataTableProps {
  gradientMap: SiteGradientMap | null;
  siteName?: string;
  showCompact?: boolean;  // Compact view for inline display
  className?: string;
}

type SortField = 'well_id' | 'wt_elev_ft' | 'lat' | 'lon';
type SortDirection = 'asc' | 'desc';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format flow direction as cardinal direction with degrees.
 */
function formatFlowDirection(degrees: number | undefined | null): string {
  if (degrees === undefined || degrees === null) return 'N/A';

  // Normalize to 0-360
  const normalized = ((degrees % 360) + 360) % 360;

  // Determine cardinal direction
  let cardinal: string;
  if (normalized >= 337.5 || normalized < 22.5) cardinal = 'N';
  else if (normalized < 67.5) cardinal = 'NE';
  else if (normalized < 112.5) cardinal = 'E';
  else if (normalized < 157.5) cardinal = 'SE';
  else if (normalized < 202.5) cardinal = 'S';
  else if (normalized < 247.5) cardinal = 'SW';
  else if (normalized < 292.5) cardinal = 'W';
  else cardinal = 'NW';

  return `${normalized.toFixed(0)}° (${cardinal})`;
}

/**
 * Format gradient magnitude as ft/ft with scientific notation if very small.
 */
function formatGradient(gradient: number | undefined | null): string {
  if (gradient === undefined || gradient === null) return 'N/A';

  if (gradient < 0.0001) {
    return gradient.toExponential(2) + ' ft/ft';
  }
  return gradient.toFixed(4) + ' ft/ft';
}

/**
 * Get compass arrow rotation CSS transform.
 */
function getCompassArrowStyle(degrees: number | undefined | null): React.CSSProperties {
  if (degrees === undefined || degrees === null) return {};
  // CSS rotation: 0 = pointing up (north), rotate clockwise
  return { transform: `rotate(${degrees}deg)` };
}

// ============================================================================
// COMPONENT
// ============================================================================

export function GradientDataTable({
  gradientMap,
  siteName,
  showCompact = false,
  className = '',
}: GradientDataTableProps) {
  const [isExpanded, setIsExpanded] = useState(!showCompact);
  const [sortField, setSortField] = useState<SortField>('well_id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Get well readings from gradient map
  const wellReadings = useMemo(() => {
    if (!gradientMap?.well_readings) return [];
    return gradientMap.well_readings.filter(
      (w) => w.lat !== undefined && w.lon !== undefined
    );
  }, [gradientMap]);

  // Sort well readings
  const sortedWellReadings = useMemo(() => {
    const sorted = [...wellReadings];
    sorted.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortField) {
        case 'well_id':
          aVal = a.well_id || '';
          bVal = b.well_id || '';
          break;
        case 'wt_elev_ft':
          aVal = a.wt_elev_ft ?? -Infinity;
          bVal = b.wt_elev_ft ?? -Infinity;
          break;
        case 'lat':
          aVal = a.lat ?? 0;
          bVal = b.lat ?? 0;
          break;
        case 'lon':
          aVal = a.lon ?? 0;
          bVal = b.lon ?? 0;
          break;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [wellReadings, sortField, sortDirection]);

  // Calculate elevation statistics
  const elevationStats = useMemo(() => {
    const elevations = wellReadings
      .map((w) => w.wt_elev_ft)
      .filter((e): e is number => e !== null && e !== undefined);

    if (elevations.length === 0) {
      return { min: null, max: null, range: null };
    }

    const min = Math.min(...elevations);
    const max = Math.max(...elevations);
    return {
      min,
      max,
      range: max - min,
    };
  }, [wellReadings]);

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort indicator
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="w-3 h-3 inline ml-1" />
    ) : (
      <ChevronDownIcon className="w-3 h-3 inline ml-1" />
    );
  };

  // No data state
  if (!gradientMap) {
    return (
      <div className={`bg-mono-900 border border-mono-800 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-mono-500">
          <BeakerIcon className="w-5 h-5" />
          <span className="text-sm">No gradient data available</span>
        </div>
        <p className="text-xs text-mono-600 mt-2">
          Upload a groundwater gradient map or wait for gradient calculation from well gauging data.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-mono-900 border border-mono-800 rounded-lg overflow-hidden ${className}`}>
      {/* Header - always visible */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-mono-850 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-5 h-5 text-blue-400" />
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
              Gradient Data
            </h3>
          </div>

          {/* Quick summary badges */}
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-mono-800 text-mono-400 border border-mono-700">
              {wellReadings.length} Wells
            </span>
            <span className="px-2 py-0.5 text-xs rounded bg-blue-900/40 text-blue-400 border border-blue-700/50">
              {gradientMap.source === 'extracted' ? 'Extracted' : 'Calculated'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Flow direction compass */}
          {gradientMap.average_flow_direction_deg !== undefined && (
            <div className="flex items-center gap-2">
              <div className="relative w-8 h-8 bg-mono-800 rounded-full border border-mono-700 flex items-center justify-center">
                <div
                  className="text-blue-400 transition-transform"
                  style={getCompassArrowStyle(gradientMap.average_flow_direction_deg)}
                >
                  <ArrowTrendingDownIcon className="w-5 h-5" />
                </div>
              </div>
              <span className="text-xs text-mono-400">
                {formatFlowDirection(gradientMap.average_flow_direction_deg)}
              </span>
            </div>
          )}

          {/* Expand/collapse */}
          <button className="p-1 rounded hover:bg-mono-800">
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-mono-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-mono-400" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-mono-800">
          {/* Summary statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-mono-850/50">
            <div className="bg-mono-900 rounded-lg p-3 border border-mono-800">
              <div className="text-[10px] text-mono-500 uppercase font-medium">Avg Gradient</div>
              <div className="text-sm font-semibold text-cyan-400 mt-1">
                {formatGradient(gradientMap.average_gradient)}
              </div>
            </div>
            <div className="bg-mono-900 rounded-lg p-3 border border-mono-800">
              <div className="text-[10px] text-mono-500 uppercase font-medium">Flow Direction</div>
              <div className="text-sm font-semibold text-blue-400 mt-1">
                {formatFlowDirection(gradientMap.average_flow_direction_deg)}
              </div>
            </div>
            <div className="bg-mono-900 rounded-lg p-3 border border-mono-800">
              <div className="text-[10px] text-mono-500 uppercase font-medium">Min Elevation</div>
              <div className="text-sm font-semibold text-mono-200 mt-1">
                {elevationStats.min !== null ? `${elevationStats.min.toFixed(2)} ft` : 'N/A'}
              </div>
            </div>
            <div className="bg-mono-900 rounded-lg p-3 border border-mono-800">
              <div className="text-[10px] text-mono-500 uppercase font-medium">Max Elevation</div>
              <div className="text-sm font-semibold text-mono-200 mt-1">
                {elevationStats.max !== null ? `${elevationStats.max.toFixed(2)} ft` : 'N/A'}
              </div>
            </div>
          </div>

          {/* Well data table */}
          {wellReadings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-mono-800 bg-mono-850/30">
                    <th
                      className="px-3 py-2 text-left font-medium text-mono-400 uppercase tracking-wider cursor-pointer hover:text-mono-200"
                      onClick={() => handleSort('well_id')}
                    >
                      Well ID
                      <SortIndicator field="well_id" />
                    </th>
                    <th
                      className="px-3 py-2 text-right font-medium text-mono-400 uppercase tracking-wider cursor-pointer hover:text-mono-200"
                      onClick={() => handleSort('wt_elev_ft')}
                    >
                      WT Elev (ft)
                      <SortIndicator field="wt_elev_ft" />
                    </th>
                    <th
                      className="px-3 py-2 text-right font-medium text-mono-400 uppercase tracking-wider cursor-pointer hover:text-mono-200"
                      onClick={() => handleSort('lat')}
                    >
                      Latitude
                      <SortIndicator field="lat" />
                    </th>
                    <th
                      className="px-3 py-2 text-right font-medium text-mono-400 uppercase tracking-wider cursor-pointer hover:text-mono-200"
                      onClick={() => handleSort('lon')}
                    >
                      Longitude
                      <SortIndicator field="lon" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedWellReadings.map((well, idx) => (
                    <tr
                      key={`${well.well_id}-${idx}`}
                      className="border-b border-mono-800/50 hover:bg-mono-850/30 transition-colors"
                    >
                      <td className="px-3 py-2 font-medium text-mono-200">
                        {well.well_id || '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-cyan-400 font-mono">
                        {well.wt_elev_ft !== null && well.wt_elev_ft !== undefined
                          ? well.wt_elev_ft.toFixed(2)
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-mono-400 font-mono">
                        {well.lat !== null && well.lat !== undefined
                          ? well.lat.toFixed(6)
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-mono-400 font-mono">
                        {well.lon !== null && well.lon !== undefined
                          ? well.lon.toFixed(6)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 text-center text-mono-500 text-sm">
              No well readings available for gradient calculation.
            </div>
          )}

          {/* Metadata footer */}
          <div className="px-3 py-2 bg-mono-850/30 border-t border-mono-800 flex items-center justify-between text-[10px] text-mono-500">
            <div className="flex items-center gap-3">
              {gradientMap.contour_interval_ft && (
                <span>Contour Interval: {gradientMap.contour_interval_ft} ft</span>
              )}
              {gradientMap.reference_datum && (
                <span>Datum: {gradientMap.reference_datum}</span>
              )}
              {gradientMap.extraction_confidence !== undefined && (
                <span>
                  Confidence: {(gradientMap.extraction_confidence * 100).toFixed(0)}%
                </span>
              )}
            </div>
            {gradientMap.created_at && (
              <span>
                Generated: {new Date(gradientMap.created_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPACT INLINE VERSION
// ============================================================================

export interface GradientSummaryInlineProps {
  gradientMap: SiteGradientMap | null;
  className?: string;
}

/**
 * Compact inline gradient summary for display in headers or sidebars.
 */
export function GradientSummaryInline({
  gradientMap,
  className = '',
}: GradientSummaryInlineProps) {
  if (!gradientMap) {
    return (
      <div className={`flex items-center gap-2 text-mono-500 text-xs ${className}`}>
        <span>No gradient data</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {/* Flow direction with compass */}
      {gradientMap.average_flow_direction_deg !== undefined && (
        <div className="flex items-center gap-1.5">
          <div
            className="text-blue-400"
            style={getCompassArrowStyle(gradientMap.average_flow_direction_deg)}
          >
            <ArrowTrendingDownIcon className="w-4 h-4" />
          </div>
          <span className="text-xs text-mono-300">
            {formatFlowDirection(gradientMap.average_flow_direction_deg)}
          </span>
        </div>
      )}

      {/* Gradient magnitude */}
      {gradientMap.average_gradient !== undefined && (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-mono-500">i =</span>
          <span className="text-xs text-cyan-400 font-mono">
            {gradientMap.average_gradient.toFixed(4)}
          </span>
        </div>
      )}

      {/* Well count */}
      {gradientMap.well_readings && gradientMap.well_readings.length > 0 && (
        <span className="text-xs text-mono-500">
          ({gradientMap.well_readings.length} wells)
        </span>
      )}
    </div>
  );
}

export default GradientDataTable;
